import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Tournament lifecycle status — mirrors `TournamentStatus` from `@bravemove/types/tournament`.
 * Kept local — `@bravemove/types` is NOT a dep of `@bravemove/ui-web`. Spec source:
 * [`docs/design/screens/F41-components.jsx`](../../../../../docs/design/screens/F41-components.jsx)
 * `tourStatusCfg` (line 8). Note `published` renders as "REG OPEN".
 */
export type TournamentStatus =
  | 'draft'
  | 'scheduled'
  | 'published'
  | 'live'
  | 'completed'
  | 'cancelled'
  | 'suspended_by_admin';
export type TournamentStatusBadgeSize = 'sm' | 'md';

export interface TournamentStatusBadgeProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  'children'
> {
  readonly status: TournamentStatus;
  readonly size?: TournamentStatusBadgeSize;
  /** Override the default status label. */
  readonly label?: string;
}

interface TournamentStatusStyle {
  readonly box: string;
  readonly dot: string;
  readonly defaultLabel: string;
  /** `live` pulses (motion-safe) to read as in-play. */
  readonly pulse: boolean;
}

const STATUS_STYLE: Record<TournamentStatus, TournamentStatusStyle> = {
  draft: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Draft',
    pulse: false,
  },
  scheduled: {
    box: 'bg-info/15 text-ink border border-info/40',
    dot: 'bg-info',
    defaultLabel: 'Scheduled',
    pulse: false,
  },
  published: {
    box: 'bg-success/15 text-ink border border-success/40',
    dot: 'bg-success',
    defaultLabel: 'Reg open',
    pulse: false,
  },
  live: {
    box: 'bg-accent/15 text-ink border border-accent/40',
    dot: 'bg-accent',
    defaultLabel: 'Live',
    pulse: true,
  },
  completed: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Completed',
    pulse: false,
  },
  cancelled: {
    box: 'bg-danger/15 text-ink border border-dashed border-danger',
    dot: 'bg-danger',
    defaultLabel: 'Cancelled',
    pulse: false,
  },
  suspended_by_admin: {
    box: 'bg-danger/15 text-ink border border-dashed border-danger',
    dot: 'bg-danger',
    defaultLabel: 'Suspended',
    pulse: false,
  },
};

const SIZE: Record<TournamentStatusBadgeSize, { box: string; dot: string }> = {
  sm: { box: 'px-[7px] py-[2px] gap-1.5 text-[9px]', dot: 'size-[5px]' },
  md: { box: 'px-[9px] py-[4px] gap-1.5 text-[10px]', dot: 'size-[6px]' },
};

/**
 * F41 — `TournamentStatusBadge` (shared primitive). Tournament lifecycle pill
 * across the organizer dashboard + public listings + admin moderation. `live`
 * pulses; `cancelled` + `suspended_by_admin` carry a dashed border. `published`
 * displays as "REG OPEN" per the canvas spec.
 *
 * @example
 *   <TournamentStatusBadge status="live" />
 *   <TournamentStatusBadge status="published" size="sm" />
 */
export function TournamentStatusBadge({
  status,
  size = 'md',
  label,
  className,
  ...rest
}: TournamentStatusBadgeProps) {
  const style = STATUS_STYLE[status];
  const sz = SIZE[size];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Tournament status: ${text}`}
      data-testid={`tournament-status-badge-${status}`}
      className={cn(
        'inline-flex items-center rounded uppercase whitespace-nowrap align-middle font-mono font-extrabold tracking-[0.1em]',
        style.box,
        sz.box,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block rounded-full shrink-0',
          sz.dot,
          style.dot,
          style.pulse && 'motion-safe:animate-pulse',
        )}
      />
      <span>{text}</span>
    </span>
  );
}
